import type { TicketEvent } from '@servicetim/shared';
import { useFieldBindings, useTicketHistory } from '@servicetim/ui/api';
import { Badge, Button, LoadingState, ProblemBanner } from '@servicetim/ui/components';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Avatar } from './badges';
import { formatDateTime, relativeTime, shortId } from './format';
import { useTicketContext } from './ticket-context';

const SYSTEM_FIELD_LABELS: Record<string, string> = {
  summary: 'Summary',
  description: 'Description',
  status_id: 'Status',
  priority_id: 'Priority',
  assignee_id: 'Assignee',
  reporter_id: 'Reporter',
  organization_id: 'Organization',
  labels: 'Labels',
  due_at: 'Due',
  parent_id: 'Parent',
  project_id: 'Project',
  issue_type_id: 'Issue type',
  resolved_at: 'Resolved',
};

const raw = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (Array.isArray(value)) return value.length === 0 ? 'empty' : value.map(String).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const Value = ({ children }: { children: ReactNode }) => (
  <span className="rounded bg-surface-sunken px-1 font-medium text-fg">{children}</span>
);

function Change({ event }: { event: TicketEvent }) {
  return (
    <>
      <Value>{event.fromDisplay ?? raw(event.from)}</Value> →{' '}
      <Value>{event.toDisplay ?? raw(event.to)}</Value>
    </>
  );
}

/** One sentence per event type, with from → to display values captured when it happened. */
function describeEvent(event: TicketEvent, fieldLabel: (key: string) => string): ReactNode {
  switch (event.type) {
    case 'created':
      return 'created the ticket';
    case 'field_changed':
      return (
        <>
          changed {fieldLabel(event.field ?? '')}: <Change event={event} />
        </>
      );
    case 'transitioned':
      return (
        <>
          moved <Change event={event} />
        </>
      );
    case 'admin_moved':
      return (
        <>
          set the status <Change event={event} /> (admin override)
        </>
      );
    case 'acknowledged':
      return 'acknowledged the ticket';
    case 'commented':
      return 'commented';
    case 'comment_edited':
      return 'edited a comment';
    case 'comment_deleted':
      return 'deleted a comment';
    case 'attachment_added':
      return (
        <>
          attached <Value>{event.toDisplay ?? 'a file'}</Value>
        </>
      );
    case 'watcher_added':
      return (
        <>
          added <Value>{event.toDisplay ?? 'a watcher'}</Value> as a watcher
        </>
      );
    case 'watcher_removed':
      return (
        <>
          removed <Value>{event.toDisplay ?? 'a watcher'}</Value> from the watchers
        </>
      );
    case 'link_added':
      return (
        <>
          linked: <Value>{event.toDisplay ?? 'another ticket'}</Value>
        </>
      );
    case 'link_removed':
      return (
        <>
          removed the link <Value>{event.toDisplay ?? ''}</Value>
        </>
      );
    case 'moved':
      return (
        <>
          moved the ticket <Change event={event} />
        </>
      );
    case 'cloned_from':
      return (
        <>
          cloned from <Value>{event.fromDisplay ?? 'another ticket'}</Value>
        </>
      );
    case 'assigned':
      return (
        <>
          assigned <Value>{event.fromDisplay ?? 'nobody'}</Value> →{' '}
          <Value>{event.toDisplay ?? 'nobody'}</Value>
        </>
      );
  }
}

/** Paged `ticket_event` rows (S-05-11), oldest first. */
export function HistoryPanel() {
  const { ticket, userLabel } = useTicketContext();
  const history = useTicketHistory(ticket.id);
  const bindings = useFieldBindings(ticket.projectId);
  const fieldLabel = (key: string): string =>
    SYSTEM_FIELD_LABELS[key] ?? bindings.data?.find((b) => b.fieldKey === key)?.fieldLabel ?? key;
  const actorLabel = (event: TicketEvent): string => {
    if (event.actor.type !== 'user') return event.actor.type;
    return (
      (event.actor.id ? userLabel(event.actor.id) : null) ??
      (event.actor.id ? shortId(event.actor.id) : 'someone')
    );
  };

  return (
    <div className="space-y-3">
      {history.error ? <ProblemBanner error={history.error} size="sm" /> : null}
      {history.isPending ? <LoadingState rows={4} label="Loading history…" /> : null}
      {history.isSuccess && history.items.length === 0 ? (
        <p className="text-fg-muted text-sm">No history yet.</p>
      ) : null}
      <ol className="space-y-2">
        {history.items.map((event) => {
          const actor = actorLabel(event);
          return (
            <li key={event.id} className="flex items-start gap-2 text-sm">
              <Avatar name={event.actor.type === 'user' ? actor : null} />
              <div className="min-w-0 flex-1">
                <p className="text-fg">
                  <span className="font-medium">{actor}</span> {describeEvent(event, fieldLabel)}
                  {event.visibility === 'internal' ? (
                    <Badge variant="warning" className="ml-2">
                      <Lock />
                      Internal
                    </Badge>
                  ) : null}
                </p>
                <time
                  dateTime={event.occurredAt}
                  title={formatDateTime(event.occurredAt)}
                  className="text-fg-muted text-xs"
                >
                  {relativeTime(event.occurredAt)} · {formatDateTime(event.occurredAt)}
                </time>
              </div>
            </li>
          );
        })}
      </ol>
      {history.hasMore ? (
        <Button
          size="sm"
          variant="outline"
          onClick={history.loadMore}
          disabled={history.isLoadingMore}
        >
          Load more
        </Button>
      ) : null}
    </div>
  );
}
